import React from "react";

/**
 * AsFeaturedIn — low-opacity press / accreditation strip.
 *
 * Drop just below a hero. Renders as styled type (no logo files needed) in
 * faded cream, with a slow horizontal drift on mobile so the strip doesn't
 * feel static. Replace with real <img> press logos as they come in — keep
 * the same outer wrapper + spacing.
 *
 * Notes for legal: these are aspirational publication names that the user
 * intends to be featured in / aligned with. Swap to real placements only
 * after coverage is confirmed.
 */
const PLACEMENTS = [
  { name: "TES", sub: "Times Educational Supplement" },
  { name: "Schools Week", sub: null },
  { name: "ABTA", sub: "Aligned" },
  { name: "TTG", sub: "Travel Trade Gazette" },
  { name: "The Guardian", sub: "Travel" },
  { name: "BS 8848", sub: "Expedition Safety" },
];

export default function AsFeaturedIn({ overline = "As featured in & aligned with" }) {
  return (
    <section
      data-testid="as-featured-in"
      className="bg-ink/60 backdrop-blur-sm border-y border-panel-border/60"
    >
      <div className="max-w-[1400px] mx-auto px-6 py-7 md:py-9">
        <div className="flex items-center justify-center gap-3 mb-5">
          <span className="h-px w-8 md:w-16 bg-cream-dim/30" />
          <p className="iq-overline text-cream-dim/70 text-center tracking-[0.3em]">
            {overline}
          </p>
          <span className="h-px w-8 md:w-16 bg-cream-dim/30" />
        </div>

        {/* Desktop: even grid · Mobile: horizontal auto-scroll marquee */}
        <div className="hidden md:flex items-center justify-between gap-8 flex-wrap">
          {PLACEMENTS.map((p) => (
            <div
              key={p.name}
              className="flex flex-col items-center text-center grayscale opacity-50 hover:opacity-90 transition-opacity duration-300"
              data-testid={`featured-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="font-display tracking-[0.18em] text-cream text-xl md:text-2xl leading-none">
                {p.name}
              </span>
              {p.sub && (
                <span className="font-serif italic text-cream-dim text-[10px] mt-1.5 tracking-wide">
                  {p.sub}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Mobile marquee */}
        <div className="md:hidden overflow-hidden">
          <div className="flex items-center gap-10 animate-ticker whitespace-nowrap py-1">
            {[...PLACEMENTS, ...PLACEMENTS].map((p, i) => (
              <div
                key={i}
                className="flex flex-col items-center opacity-50"
              >
                <span className="font-display tracking-[0.18em] text-cream text-lg leading-none">
                  {p.name}
                </span>
                {p.sub && (
                  <span className="font-serif italic text-cream-dim text-[10px] mt-1 tracking-wide">
                    {p.sub}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <p className="font-serif italic text-cream-dim/60 text-[11px] text-center mt-4">
          Selected press &amp; industry standards. Logos available on request to verified school groups.
        </p>
      </div>
    </section>
  );
}
